import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import FeedbackForm from './components/FeedbackForm';

const values = [
  {
    icon: 'ri-compass-3-line',
    title: '清晰的浏览路径',
    desc: '从频道到作品，每一步都有明确入口。分类、筛选、排序与分页配合，让查找不再依赖运气。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '信息优先，不打扰',
    desc: '页面只保留作品本身需要被看到的信息：简介、主创、年份与标签，不放置冗余弹窗与广告位。',
  },
  {
    icon: 'ri-heart-3-line',
    title: '把选择权交给你',
    desc: '收藏由你决定，片单保存在本机。推荐只作为参考，最终看什么由你说了算。',
  },
];

const contentScope = [
  { type: 'movie', label: '电影', desc: '剧情长片、类型佳作、动画与纪录类作品' },
  { type: 'tv', label: '电视剧', desc: '都市、家庭、古装、悬疑等长剧集与短剧' },
  { type: 'variety', label: '综艺', desc: '美食、音乐、竞技、访谈与文化类节目' },
];

export default function AboutPage() {
  return (
    <SiteLayout
      title="关于我们 - 丝瓜视频"
      description="了解丝瓜视频的平台定位、内容范围与联系渠道，以及我们对影视浏览体验的设计理念。"
      keywords="关于丝瓜视频,丝瓜视频介绍,影视平台,内容范围"
    >
      <main className="flex-1 w-full pb-16">
        <section className="relative w-full overflow-hidden bg-foreground-950">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20abstract%20cinematic%20background%20with%20soft%20amber%20light%20gradients%2C%20film%20strip%20texture%20in%20the%20background%2C%20minimal%20elegant%20composition%2C%20dark%20charcoal%20tones%2C%20artistic%20atmosphere&width=1600&height=520&seq=sigua-about-hero&orientation=landscape"
            alt="丝瓜视频平台介绍"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/20"></div>
          <div className="relative px-4 md:px-8 py-14 md:py-20">
            <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-white/65 mb-4">
              <Link to="/" className="hover:text-white transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-white">关于我们</span>
            </nav>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/12 border border-white/20 text-xs text-white">
              <i className="ri-leaf-line"></i> Sigua TV
            </span>
            <h1 className="mt-4 font-heading text-3xl md:text-5xl font-black text-white tracking-tight max-w-2xl leading-tight">
              为影迷而做的影视内容门户
            </h1>
            <p className="mt-4 text-sm md:text-base text-white/75 max-w-2xl leading-relaxed">
              丝瓜视频聚合电影、电视剧与综艺三类内容，把作品信息整理成清晰、可筛选、可收藏的浏览方式，让找片这件事变得简单而愉快。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 mt-10 md:mt-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
            {values.map((item) => (
              <div
                key={item.title}
                className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6"
              >
                <span className="w-10 h-10 rounded-md bg-primary-500 text-background-50 flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-lg`}></i>
                </span>
                <h2 className="font-heading text-base font-bold text-foreground-950">{item.title}</h2>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-8 mt-10 md:mt-14">
          <div className="flex flex-col lg:flex-row gap-5 lg:gap-6">
            <div className="flex-1 min-w-0 rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
                <h2 className="font-heading text-lg md:text-xl font-black text-foreground-950">
                  内容范围
                </h2>
              </div>
              <p className="text-sm text-foreground-600 leading-7">
                平台当前覆盖三类影视内容，每部作品都包含类型、主创、年份、集数或时长、剧情简介与内容标签等基础信息，方便快速判断是否符合自己的口味。
              </p>
              <div className="mt-5 space-y-3">
                {contentScope.map((scope) => (
                  <Link
                    key={scope.type}
                    to={`/category/${scope.type}`}
                    className="flex items-center gap-4 rounded-md bg-background-100 border border-background-200 px-4 py-3 hover:border-primary-300 transition-colors"
                  >
                    <span className="w-9 h-9 rounded-md bg-background-50 text-primary-600 flex items-center justify-center shrink-0">
                      <i
                        className={`${
                          scope.type === 'movie'
                            ? 'ri-film-line'
                            : scope.type === 'tv'
                              ? 'ri-tv-2-line'
                              : 'ri-mic-line'
                        } text-base`}
                      ></i>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-foreground-950">{scope.label}</p>
                      <p className="text-xs text-foreground-500 mt-0.5">{scope.desc}</p>
                    </div>
                    <i className="ri-arrow-right-line text-foreground-400"></i>
                  </Link>
                ))}
              </div>

              <div className="mt-6 rounded-md bg-accent-50 border border-accent-200 p-4">
                <h3 className="text-sm font-bold text-accent-900 flex items-center gap-2">
                  <i className="ri-error-warning-line"></i> 内容与版权说明
                </h3>
                <p className="mt-2 text-xs text-accent-900/80 leading-relaxed">
                  本站为影视门户演示站点，站内作品名称、导演、演员、剧情及评分均为虚构示例，与现实中任何影视作品或从业人员无关。平台不提供真实视频播放、下载或资源分发服务。若作为正式产品上线，需自行取得相应内容的合法授权。
                </p>
              </div>
            </div>

            <div className="w-full lg:w-[380px] shrink-0 space-y-4">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h3 className="font-heading text-sm font-bold text-foreground-950 mb-3">联系我们</h3>
                <div className="space-y-2.5 text-sm">
                  <p className="flex items-center gap-2 text-foreground-700">
                    <i className="ri-mail-line text-primary-500"></i>
                    <span className="font-label">contact@siguatv.demo</span>
                  </p>
                  <p className="flex items-center gap-2 text-foreground-700">
                    <i className="ri-time-line text-primary-500"></i>
                    工作日 10:00 – 18:00 回复
                  </p>
                  <p className="flex items-center gap-2 text-foreground-700">
                    <i className="ri-map-pin-line text-primary-500"></i>
                    线上内容平台 · 无实体门店
                  </p>
                </div>
                <p className="mt-3 text-[11px] text-foreground-500 leading-relaxed">
                  以上邮箱为演示地址，仅用于页面展示。
                </p>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading text-sm font-bold text-foreground-950 mb-2">快捷入口</h3>
                <div className="grid grid-cols-2 gap-2">
                  <Link
                    to="/category/movie"
                    className="px-3 py-2 rounded-md bg-background-100 text-xs text-foreground-800 hover:text-primary-600 transition-colors"
                  >
                    电影频道
                  </Link>
                  <Link
                    to="/category/tv"
                    className="px-3 py-2 rounded-md bg-background-100 text-xs text-foreground-800 hover:text-primary-600 transition-colors"
                  >
                    电视剧频道
                  </Link>
                  <Link
                    to="/category/variety"
                    className="px-3 py-2 rounded-md bg-background-100 text-xs text-foreground-800 hover:text-primary-600 transition-colors"
                  >
                    综艺频道
                  </Link>
                  <Link
                    to="/favorites"
                    className="px-3 py-2 rounded-md bg-background-100 text-xs text-foreground-800 hover:text-primary-600 transition-colors"
                  >
                    我的收藏
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 mt-10 md:mt-14">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
            <h2 className="font-heading text-lg md:text-xl font-black text-foreground-950">
              用户反馈
            </h2>
          </div>
          <p className="text-sm text-foreground-600 mb-4 max-w-2xl leading-relaxed">
            想看什么类型的作品、希望增加哪些筛选条件，或者页面体验上有任何建议，都可以写在这里告诉我们。
          </p>
          <FeedbackForm />
        </section>
      </main>
    </SiteLayout>
  );
}